// Computes where pdfmake breaks a cover page across pages, mapped back to the
// editor's nodes, so the editor can draw "Page N" markers.
//
// pdfmake owns all layout/pagination — we don't re-implement it. Two passes:
//   1. Block pass: tag each emitted top-level block, observe which page it starts
//      on via pdfmake's `pageBreakBefore`. Gives block-level break points.
//   2. Word pass: when a paragraph/heading STRADDLES a page fold, binary-search
//      the number of its words that fit on the earlier page (rendering trimmed
//      variants + a sentinel). Greedy line-wrapping is prefix-stable, so trimming
//      doesn't change how the kept words wrap — the result is accurate.
import type { Content as TipTapContent } from "@tiptap/react";

import { buildCoverDocDefinition, type BuildCoverOptions } from "./index";

export interface CoverPageBreak {
  /** The page number that begins at this break (2 = first overflow). */
  page: number;
  /** Editor top-level node the marker attaches to. */
  nodeIndex: number;
  /**
   * If set, the break falls INSIDE `nodeIndex` at this character offset within the
   * node's content (place the marker there). If absent, the break is block-aligned
   * — place the marker BEFORE `nodeIndex`.
   */
  charOffset?: number;
}

type Opts = BuildCoverOptions;

// ── pdfmake plumbing ────────────────────────────────────────────────────────
let pdfMakePromise: Promise<{ createPdf: any; vfs: any }> | null = null;
async function getPdfMake() {
  if (!pdfMakePromise) {
    pdfMakePromise = (async () => {
      const m: any = await import("pdfmake/build/pdfmake");
      const v: any = await import("pdfmake/build/vfs_fonts");
      return {
        createPdf: m.createPdf ?? m.default?.createPdf,
        vfs: v.vfs ?? v.default ?? v,
      };
    })();
  }
  return pdfMakePromise;
}

async function renderToLayout(doc: any): Promise<void> {
  const { createPdf, vfs } = await getPdfMake();
  await new Promise<void>((resolve, reject) => {
    try {
      createPdf(doc, undefined, undefined, vfs).getBuffer(() => resolve());
    } catch (ex) {
      reject(ex);
    }
  });
}

// ── node mapping helpers ──────────────────────────────────────────────────────
/**
 * Replays the converter's skip rules so doc.content[j] lines up with kept[j]. Also records,
 * as positions in `kept` (not raw node indices), which kept node immediately followed an
 * explicit editor `pageBreak` — see `explicitBreakAt` below for why that's needed separately
 * from pdfmake's own `pageBreakBefore` callback.
 */
function keptNodeIndices(nodes: any[]): { kept: number[]; explicitBreakAt: Set<number> } {
  const kept: number[] = [];
  const explicitBreakAt = new Set<number>();
  let sawPageBreak = false;

  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    if (!el || typeof el !== "object") continue;
    if (el.type === "pageBreak") {
      sawPageBreak = true;
      continue;
    }
    if (el.type === "paragraph" && i === nodes.length - 1 && !el.content) continue;

    if (sawPageBreak) {
      explicitBreakAt.add(kept.length);
      sawPageBreak = false;
    }
    kept.push(i);
  }

  return { kept, explicitBreakAt };
}

/** Headings/paragraphs/lines come back as arrays; columns/images/signatures as objects. */
function tagItem(item: any, id: string): void {
  if (Array.isArray(item)) {
    if (item.length && item[0] && typeof item[0] === "object") item[0].id = id;
  } else if (item && typeof item === "object") {
    item.id = id;
  }
}

// ── inline word tokenisation (for the word-level probe) ───────────────────────
type Unit =
  | { kind: "word" | "space"; text: string; marks?: any[]; pmLen: number }
  | { kind: "mention" | "break" | "other"; node: any; pmLen: number };

const isWordUnit = (u: Unit) => u.kind === "word" || u.kind === "mention";

/** Tokenise a node's inline content into word/space/mention/break units. */
function tokenizeInline(content: any[]): Unit[] {
  const units: Unit[] = [];
  if (!Array.isArray(content)) return units;

  for (const node of content) {
    if (node?.type === "text" && typeof node.text === "string") {
      // each match is a non-space run (+ trailing spaces) OR a leading whitespace run
      const re = /\S+\s*|\s+/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(node.text))) {
        const chunk = m[0];
        const isWord = /\S/.test(chunk);
        units.push({ kind: isWord ? "word" : "space", text: chunk, marks: node.marks, pmLen: chunk.length });
      }
    } else if (node?.type === "mention") {
      units.push({ kind: "mention", node, pmLen: 1 });
    } else if (node?.type === "hardBreak") {
      units.push({ kind: "break", node, pmLen: 1 });
    } else if (node) {
      units.push({ kind: "other", node, pmLen: 1 });
    }
  }
  return units;
}

const totalWordUnits = (units: Unit[]) => units.reduce((n, u) => n + (isWordUnit(u) ? 1 : 0), 0);

// ── list-item straddle detection ──────────────────────────────────────────────
const isListNode = (node: any) => node?.type === "bulletList" || node?.type === "orderedList";

/**
 * ProseMirror's own position-size formula, replayed on the JSON directly (no live doc needed):
 * a leaf/atom node (no content array — mention, hardBreak, pageBreak, image, ...) occupies 1
 * position; anything with content occupies 2 (its own open/close tokens) plus its children's
 * sizes; text occupies its string length. Matches what `doc.forEach`'s real `node.nodeSize`
 * would report for the same structure, which is what buildDecorations positions markers against.
 */
function pmSize(node: any): number {
  if (!node || typeof node !== "object") return 0;
  if (node.type === "text") return typeof node.text === "string" ? node.text.length : 0;
  if (!Array.isArray(node.content)) return 1;
  return 2 + node.content.reduce((sum: number, c: any) => sum + pmSize(c), 0);
}

/** PM offset (relative to the list node's own content start) of the start of listItem `index`. */
function offsetToListItemStart(listItems: any[], index: number): number {
  let offset = 0;
  for (let i = 0; i < index; i++) offset += pmSize(listItems[i]);
  return offset;
}

/**
 * pdf-report-core's getListItems flattens each TListItem's children into the pdfmake items
 * array 1:1 per paragraph/heading/nested-list child (see custom-section-builder.ts) — usually
 * one pdfmake entry per listItem, but more if an item has multiple children (e.g. text + a
 * nested sublist). Mirrors that same flattening locally, purely to map each pdfmake entry back
 * to which original listItem it came from. If pdf-report-core's own flattening rule ever changes,
 * this only degrades to a misattributed (not a crashing) tag.
 */
function mapPdfListItemsToTiptapIndices(tiptapListItems: any[]): number[] {
  const owners: number[] = [];
  for (let i = 0; i < tiptapListItems.length; i++) {
    const li = tiptapListItems[i];
    if (li?.type !== "listItem") continue;
    const children = Array.isArray(li.content) ? li.content : [];
    for (const child of children) {
      if (["paragraph", "heading", "bulletList", "orderedList"].includes(child?.type)) {
        owners.push(i);
      }
    }
  }
  return owners;
}

/**
 * DMS-2027: a list is tagged as ONE opaque top-level block above, so a page split happening
 * INSIDE it (pdfmake happily breaks a list between items) was invisible to the block-level
 * pageByJ mapping and got attributed to whatever node came AFTER the whole list instead. This
 * walks the list's own per-item start pages (tagged in Pass 1 below) to find exactly which
 * original list item each internal transition falls at.
 *
 * Scope: attributes the break to the START of that list item (block-aligned within the list),
 * not a word inside it — the same tier of accuracy already used for any other non-paragraph/
 * heading straddle in this file (tables, images, columns). A listItem whose own content straddles
 * mid-paragraph shows the marker at the top of that item rather than the exact wrapped line —
 * still a real improvement over "after the whole list", short of full word-level parity.
 */
function emitListItemBreaks(
  breaks: CoverPageBreak[],
  editorIndex: number,
  listNode: any,
  itemPages: Map<number, number> | undefined,
  lastPage: number
): number {
  const tiptapItems: any[] = Array.isArray(listNode?.content) ? listNode.content : [];
  if (!tiptapItems.length || !itemPages) return lastPage;

  let running = lastPage;
  for (let i = 0; i < tiptapItems.length; i++) {
    const page = itemPages.get(i);
    if (typeof page !== "number" || page <= running) continue;
    breaks.push({ page, nodeIndex: editorIndex, charOffset: offsetToListItemStart(tiptapItems, i) });
    running = page;
  }
  return running;
}

/** Rebuild inline content containing the first `wordLimit` word/mention units. */
function reconstructInline(units: Unit[], wordLimit: number): any[] {
  const content: any[] = [];
  let words = 0;
  for (const u of units) {
    if (words >= wordLimit && isWordUnit(u)) break;
    if (u.kind === "word" || u.kind === "space") {
      const text: any = { type: "text", text: u.text };
      if (u.marks) text.marks = u.marks;
      content.push(text);
      if (u.kind === "word") words++;
    } else if ("node" in u) {
      content.push(u.node);
      if (u.kind === "mention") words++;
    }
  }
  return content;
}

/** PM character offset (within node content) after the first `wordLimit` words. */
function pmOffsetAfterWords(units: Unit[], wordLimit: number): number {
  let words = 0;
  let offset = 0;
  for (const u of units) {
    if (words >= wordLimit && isWordUnit(u)) break;
    offset += u.pmLen;
    if (isWordUnit(u)) words++;
  }
  return offset;
}

// ── the word-level probe ──────────────────────────────────────────────────────
const SENTINEL_ID = "__pb_sentinel__";

/** Page the sentinel lands on when `straddle` is trimmed to `k` words. */
async function sentinelPageForWords(
  nodes: any[],
  straddleEditorIndex: number,
  units: Unit[],
  k: number,
  opts: Opts
): Promise<number> {
  const straddle = nodes[straddleEditorIndex];
  const truncated = { ...straddle, content: reconstructInline(units, k) };
  const probeNodes = [...nodes.slice(0, straddleEditorIndex), truncated];

  const doc: any = await buildCoverDocDefinition(probeNodes as TipTapContent, opts);
  const items = Array.isArray(doc.content) ? doc.content : [doc.content];
  items.push({ text: "​", id: SENTINEL_ID, fontSize: 1, margin: [0, 0, 0, 0] });
  doc.content = items;

  let sentinelPage = 1;
  doc.pageBreakBefore = (n: any) => {
    if (n?.id === SENTINEL_ID && typeof n.startPosition?.pageNumber === "number") {
      sentinelPage = n.startPosition.pageNumber;
    }
    return false;
  };

  await renderToLayout(doc);
  return sentinelPage;
}

/**
 * Largest word count whose sentinel still sits on `priorPage` (the page before the
 * fold). Binary search; monotonic because adding words only pushes the sentinel down.
 */
async function wordsOnPriorPage(
  nodes: any[],
  straddleEditorIndex: number,
  units: Unit[],
  priorPage: number,
  opts: Opts
): Promise<number> {
  const total = totalWordUnits(units);
  let lo = 0; // 0 words always fits on priorPage
  let hi = total; // may or may not fit
  // If the whole thing fits, there's no mid-block fold.
  if ((await sentinelPageForWords(nodes, straddleEditorIndex, units, total, opts)) <= priorPage) {
    return total;
  }
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const page = await sentinelPageForWords(nodes, straddleEditorIndex, units, mid, opts);
    if (page <= priorPage) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

// ── public entry ──────────────────────────────────────────────────────────────
export async function computeCoverPageBreaks(content: TipTapContent, opts: Opts): Promise<CoverPageBreak[]> {
  const nodes: any[] = Array.isArray(content) ? content : (content as any)?.content ?? [];
  if (!nodes.length) return [];

  // ── Pass 1: block-level start pages ──
  const doc: any = await buildCoverDocDefinition(content, opts);
  const items = Array.isArray(doc.content) ? doc.content : [doc.content];
  const { kept, explicitBreakAt } = keptNodeIndices(nodes);

  const listItemPageByJ = new Map<number, Map<number, number>>();

  items.forEach((item: any, j: number) => {
    const editorIndex = kept[j];
    if (editorIndex === undefined) return;
    tagItem(item, `pb-${j}`);

    const node = nodes[editorIndex];
    if (isListNode(node)) {
      const listObj = Array.isArray(item) ? item[0] : item;
      const pdfListItems: any[] = listObj?.ul || listObj?.ol || [];
      const owners = mapPdfListItemsToTiptapIndices(Array.isArray(node.content) ? node.content : []);
      pdfListItems.forEach((entry: any, k: number) => {
        const owner = owners[k];
        if (owner !== undefined) tagItem(entry, `pb-${j}-li-${owner}`);
      });
      listItemPageByJ.set(j, new Map());
    }
  });

  const pageByJ = new Map<number, number>();
  doc.pageBreakBefore = (currentNode: any) => {
    const id: string | undefined = currentNode?.id;
    const pageNumber = currentNode?.startPosition?.pageNumber;
    if (typeof pageNumber !== "number" || !id) return false;

    const liMatch = /^pb-(\d+)-li-(\d+)$/.exec(id);
    if (liMatch) {
      const perItemMap = listItemPageByJ.get(Number(liMatch[1]));
      const i = Number(liMatch[2]);
      if (perItemMap && !perItemMap.has(i)) perItemMap.set(i, pageNumber);
      return false;
    }

    if (id.startsWith("pb-") && !pageByJ.has(Number(id.slice(3)))) {
      pageByJ.set(Number(id.slice(3)), pageNumber);
    }
    return false;
  };
  await renderToLayout(doc);

  // ── Pass 2: for each page transition, refine to a word boundary if possible ──
  const breaks: CoverPageBreak[] = [];
  let lastPage = 1;

  for (let j = 0; j < items.length; j++) {
    const editorIndex = kept[j];
    if (editorIndex === undefined) continue;

    if (explicitBreakAt.has(j)) {
      // pdfmake's pageBreakBefore callback never fires for a node that already carries its own
      // manual pageBreak: "before" (confirmed by direct probe against pdfmake) — so the normal
      // pageByJ lookup below would miss this break entirely and the loop would misattribute it
      // to whichever node happens to follow, drawing the marker after the broken-to node instead
      // of before it. An explicit break always lands at the very top of the next page relative
      // to wherever the preceding content ended, regardless of that content's own height.
      const page = Math.max(lastPage + 1, pageByJ.get(j) ?? 0);
      breaks.push({ page, nodeIndex: editorIndex });
      lastPage = page;
    } else {
      const page = pageByJ.get(j);
      if (typeof page === "number" && page > lastPage) {
        const straddleEditorIndex = j > 0 ? kept[j - 1] : undefined;
        const straddle = straddleEditorIndex !== undefined ? nodes[straddleEditorIndex] : undefined;

        let refined = false;
        if (straddle && (straddle.type === "paragraph" || straddle.type === "heading")) {
          try {
            const units = tokenizeInline(straddle.content ?? []);
            if (totalWordUnits(units) > 1) {
              const fitWords = await wordsOnPriorPage(nodes, straddleEditorIndex!, units, page - 1, opts);
              if (fitWords < totalWordUnits(units)) {
                breaks.push({ page, nodeIndex: straddleEditorIndex!, charOffset: pmOffsetAfterWords(units, fitWords) });
                refined = true;
              }
            }
          } catch {
            // fall back to block-aligned below
          }
        }

        if (!refined) breaks.push({ page, nodeIndex: editorIndex });
        lastPage = page;
      }
    }

    // A list's own overall start (handled above like any other block) doesn't tell us about a
    // split happening INSIDE it — check its per-item start pages separately, regardless of
    // whether the block-level check above fired.
    if (isListNode(nodes[editorIndex])) {
      lastPage = emitListItemBreaks(breaks, editorIndex, nodes[editorIndex], listItemPageByJ.get(j), lastPage);
    }
  }

  return breaks;
}
